import React, { useState, useCallback, useEffect } from 'react';
import { connect } from "react-redux";
import { Card, TextContainer, Heading, Button, Toast, Banner, Tabs, Stack, DisplayText, Badge, Page, Spinner } from '@shopify/polaris';
import { useHistory } from 'react-router-dom';
import { updateOnboarding } from '../../redux/actions/onboardingActions';
import { OnboardingStatus } from '../../constants/common';
import settingsApi from '../../services/settingsApi';
import pricingApi from '../../services/pricingApi';
import { CircleInformationMajor, AbandonedCartMajor } from '@shopify/polaris-icons';

const PricingPlanContainer = ({ updateOnboarding, ...props }) => {
  const { error } = props;
  const [selectedTab, setSelectedTab] = useState(1);
  const [toast, setToast] = useState(false);
  const [apiCalled, setApiCalled] = useState(false);
  const [toastMessage, setToastMessage] = useState('Something went wrong!');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [currentSettings, setCurrentSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const history = useHistory();

  const tabs = [
    {
      id: 'monthly',
      content: 'Monthly',
      accessibilityLabel: 'Monthly plan',
      panelID: 'monthly-content',
    },
    {
      id: 'yearly',
      content: 'Yearly',
      accessibilityLabel: 'Yearly plan',
      panelID: 'yearly-content',
    },
  ];

  const features = [
    'Multi-currency pricing support',
    'Country-specific pricing',
    'Automatic currency conversion',
    'Real-time exchange rates',
    // 'Advanced analytics',
    'Priority customer support',
    'Custom integration support',
    'Sell at different prices per country',
    'IP auto-detects & locks customer price',
    'No theme collaboration needed',
    'Percentage or fixed price override',
    'Configure your currencies',
    'Shopify Payments Supported',
    'Currency Rounding Rules',
    'Bulk price updates using CSV'
  ];

  // Fetch pricing plans from API
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        setLoadingPlans(true);
        const response = await pricingApi.fetchPlan();
        if (response.data && response.data.length > 0) {
          setPricingPlans(response.data);
        } else {
          setToastMessage('No pricing plans available');
          setToast(true);
        }
      } catch (error) {
          setToastMessage(
            (typeof error?.error?.error?.data === "string")
              ? `Failed to fetch pricing plans - ${error.error.error.data}`
              : "Failed to fetch pricing plans"
          );
        setToast(true);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPricingPlans();
  }, []);

  // Fetch current settings to check active plan
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoadingSettings(true);
        const response = await settingsApi.getSettings();
        if (response.data) {
          setCurrentSettings(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (error && error.error) {
      if (error.error !== "Invalid Session") {
        if (typeof error?.error === "string") {
          setToastMessage(error.error);
        }
        setToast(true);
      }
    }
  }, [error])

  const handleTabChange = useCallback((selectedTabIndex) => {
    if (apiCalled) return;  // Prevent tab changes during API calls
    setSelectedTab(selectedTabIndex);
  }, [apiCalled]);

  const getPlanByType = (type) => {
    return pricingPlans.find(plan => plan.type === type);
  };

  const getPlanById = (id) => {
    return pricingPlans.find(plan => String(plan.id) === String(id));
  };

  // Check if a plan is currently active by matching plan ID
  const isCurrentActivePlan = (planType) => {
    if (!currentSettings || currentSettings.chargeStatus !== "active" || !currentSettings.chargeType) {
      return false;
    }

    // Get the plan by type
    const plan = getPlanByType(planType);
    if (!plan) return false;
    // Check if the current active plan ID matches this plan's ID
    // Convert both to string for comparison to handle type differences
    return String(["usage_charge", "monthly"].includes(currentSettings.chargeType) ? "monthly" : "yearly") === String(plan.type);
  };

  // Get the current active plan details
  const getCurrentActivePlan = () => {
    if (!currentSettings || currentSettings.chargeStatus !== "active" || !currentSettings.chargeType) {
      return null;
    }

    return getPlanByType(String(["usage_charge", "monthly"].includes(currentSettings.chargeType ? "monthly" : "yearly")));
  };

  // Get the current active plan type (monthly/yearly)
  const getCurrentActivePlanType = () => {
    const activePlan = getCurrentActivePlan();
    return activePlan ? activePlan.type : null;
  };

  // Handle plan selection and API call
  const handlePlanSelection = (planType) => {
    setApiCalled(true);

    const selectedPlan = getPlanByType(planType);
    if (!selectedPlan) {
      setToastMessage('Selected plan not found');
      setToast(true);
      setApiCalled(false);
      return;
    }

    // Check if this is already the active plan
    if (isCurrentActivePlan(planType)) {
      setToastMessage('This is already your current active plan!');
      setToast(true);
      setApiCalled(false);
      return;
    }

    if (process.env.REACT_APP_ENV !== 'local') {
      pricingApi.applyPlan(selectedPlan.id, {}).then((url) => {
        if (url.data && url.data.url) {
          // window.location.href = url.data.url;
          window.location.assign(url.data.url);
        } else {
          setToastMessage('Failed to redirect to payment page');
          setToast(true);
          setApiCalled(false);
        }
      })
        .catch(error => {
            setToastMessage(
              (typeof error?.error?.error?.data === "string")
                ? `Failed to apply plan. Please try again. - ${error.error.error.data}`
                : "Failed to apply plan. Please try again."
            );
          setToast(true);
          setApiCalled(false);
        })
    } else {
      // Local environment - show success message
      setToastMessage(`Successfully updated to ${planType} plan!`);
      setToast(true);
      setApiCalled(false);
    }
  }

  const toggleActive = useCallback(() => setToast((toast) => !toast), []);

  const toastMarkup = toast ? (
    <Toast
      content={toastMessage}
      duration={3000}
      error={toastMessage.includes('Failed') || toastMessage.includes('Something went wrong')}
      onDismiss={toggleActive}
    />
  ) : null;

  const renderPricingCard = (planType, isPopular = false) => {
    const plan = getPlanByType(planType);
    if (!plan) return null;

    const isYearly = planType === 'yearly';
    const monthlyPlan = getPlanByType('monthly');
    const isActivePlan = isCurrentActivePlan(planType);

    // Calculate savings for yearly plan with proper decimal formatting
    const yearlyOriginalPrice = monthlyPlan ? monthlyPlan.price * 12 : null;
    const yearlyDiscount = yearlyOriginalPrice && isYearly ?
      Math.round(((yearlyOriginalPrice - plan.price) / yearlyOriginalPrice) * 100) : 0;
    const yearlySavings = yearlyOriginalPrice && isYearly ?
      (yearlyOriginalPrice - plan.price).toFixed(2) : 0;

    return (
      <Card sectioned>
        <div style={{ textAlign: 'center', position: 'relative' }}>
          {isPopular && !isActivePlan && (
            <div style={{ position: 'absolute', top: '-5%', left: '50%', transform: 'translateX(-50%)' }}>
              <Badge status="success">Most Popular</Badge>
            </div>
          )}

          {isActivePlan && (
            <div style={{ position: 'absolute', top: '-5%', left: '50%', transform: 'translateX(-50%)' }}>
              <Badge status="info">Current Plan</Badge>
            </div>
          )}

          <Stack vertical spacing="loose">
            <Stack vertical spacing="tight">
              <Heading element="h2">
                {plan.name} - {isYearly ? 'Yearly' : 'Monthly'}
              </Heading>

              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: '8px', position: 'relative' }}>
                <DisplayText size="large" element="span">
                  ${plan.price}
                </DisplayText>
                <span style={{ fontSize: '16px', color: '#637381' }}>
                  /{isYearly ? 'year' : 'month'}
                </span>
                {isYearly && yearlyOriginalPrice && yearlyDiscount > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{ textDecoration: 'line-through', color: '#637381', fontSize: '14px' }}>
                      ${yearlyOriginalPrice.toFixed(2)}/year
                    </span>
                    <Badge status="success">{yearlyDiscount}% OFF</Badge>
                  </div>
                )}
              </div>
              {/* {isYearly && yearlyOriginalPrice && yearlyDiscount > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={{ textDecoration: 'line-through', color: '#637381' }}>
                    ${yearlyOriginalPrice.toFixed(2)}/year
                  </span>
                  <Badge status="success">{yearlyDiscount}% OFF</Badge>
                </div>
              )} */}

              {/* {isYearly && yearlySavings > 0 && (
                <p style={{ color: '#008060', fontWeight: '500', margin: '8px 0' }}>
                  Save ${yearlySavings} per year!
                </p>
              )} */}

              {/* {plan.trialDays && plan.trialDays > 0 && !isActivePlan && (
                <p style={{ color: '#008060', fontWeight: '500', margin: '8px 0' }}>
                  {plan.trialDays} days free trial included!
                </p>
              )} */}
            </Stack>

            <div style={{ textAlign: 'left', maxWidth: '300px', margin: '0 auto' }}>
              <Heading element="h4" size="small">Features included:</Heading>
              <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
                {features.map((feature, index) => (
                  <li key={index} style={{ fontSize: '14px', marginBottom: '8px' }}>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              primary={!isActivePlan}
              disabled={isActivePlan || apiCalled}
              loading={apiCalled && !isActivePlan}
              size="large"
              onClick={() => handlePlanSelection(planType)}
              fullWidth
              style={{
                backgroundColor: isActivePlan ? '#9ca3af' : undefined,
                borderColor: isActivePlan ? '#9ca3af' : undefined,
                color: isActivePlan ? 'white' : undefined
              }}
            >
              {isActivePlan ? 'Current Plan' : apiCalled ? 'Processing...' : isYearly ? 'Upgrade Plan' : 'Downgrade Plan'}
            </Button>
          </Stack>
        </div>
      </Card>
    );
  };

  // Show loading state while fetching plans or settings
  if (loadingPlans || loadingSettings) {
    return (
      <Page title="Pricing Plans" subtitle="Choose the perfect plan for your business needs">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spinner size="large" />
          <p style={{ marginTop: '16px', color: '#637381' }}>Loading pricing plans...</p>
        </div>
      </Page>
    );
  }

  // Show error state if no plans available
  if (pricingPlans.length === 0) {
    return (
      <Page title="Pricing Plans" subtitle="Choose the perfect plan for your business needs">
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <p style={{ color: '#637381' }}>No pricing plans available at the moment.</p>
          <Button onClick={() => window.location.reload()} style={{ marginTop: '16px' }} disabled={apiCalled}>
            Retry
          </Button>
        </div>
        {toastMarkup}
      </Page>
    );
  }

  const monthlyPlan = getPlanByType('monthly');
  const yearlyPlan = getPlanByType('yearly');
  const yearlyOriginalPrice = monthlyPlan ? monthlyPlan.price * 12 : null;
  const yearlyDiscount = yearlyOriginalPrice && yearlyPlan ?
    Math.round(((yearlyOriginalPrice - yearlyPlan.price) / yearlyOriginalPrice) * 100) : 0;

  const currentActivePlan = getCurrentActivePlan();
  const currentActivePlanType = getCurrentActivePlanType();

  return (
    <Page
      title="Pricing Plans"
      subtitle="Choose the perfect plan for your business needs"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Current Plan Status */}
        {currentSettings && currentSettings.chargeStatus === "active" && currentActivePlan && (
          <div style={{ marginBottom: '24px' }}>
            <Card sectioned>
              <Banner status="success" icon={CircleInformationMajor}>
                <p>
                  You currently have an active <strong>{currentActivePlanType}</strong> subscription.
                  You can update your plan below.
                </p>
              </Banner>
            </Card>
          </div>
        )}

        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <p style={{ fontSize: '16px', color: '#637381' }}>
            Select between Monthly or Yearly plan{yearlyDiscount > 0 && ` with a ${yearlyDiscount}% discount on Yearly plan`}
          </p>
        </div>

        {/* Pricing Plans Tabs */}
        <div className="parent-tab-container" style={{ display: "flex", justifyContent: "center", margin: '20px 0' }}>
          <Card>
            <div style={{ textAlign: 'center', alignContent: 'center' }}>
              <div style={{ display: 'inline-block' }}>
                <div style={{ margin: "auto" }}>
                  <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} disabled={apiCalled} fitted >

                    <Card.Section >
                      {selectedTab === 0 && monthlyPlan && (
                        <div style={{ padding: '10px 0' }}>
                          {renderPricingCard('monthly')}
                        </div>
                      )}

                      {selectedTab === 1 && yearlyPlan && (
                        <div style={{ padding: '10px 0' }}>
                          {renderPricingCard('yearly', true)}
                        </div>
                      )}
                    </Card.Section>
                  </Tabs>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Comparison Section */}
        {monthlyPlan && yearlyPlan && (
          <div className="parent-plan-compare" style={{ display: "flex", justifyContent: "center", marginTop: '40px 0' }}>
            <Card sectioned>
              <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'inline-block' }}>
                  <Heading element="h3">Plan Comparison</Heading>
                  <div style={{ marginTop: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '600px', margin: '20px auto 0' }}>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTabChange(0)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTabChange(0);
                        }
                      }}
                      style={{
                        padding: '16px',
                        border: selectedTab === 0 || isCurrentActivePlan('monthly') ? '2px solid #008060' : '1px solid #e1e3e5',
                        borderRadius: '8px',
                        position: 'relative',
                        cursor: apiCalled ? 'not-allowed' : 'pointer',
                        opacity: apiCalled ? 0.6 : 1,
                        backgroundColor: selectedTab === 0 ? '#f1f8f5' : '#fff',
                      }}
                    >
                      {isCurrentActivePlan('monthly') && (
                        <div style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)' }}>
                          <Badge status="info">Current</Badge>
                        </div>
                      )}
                      <Heading element="h4" size="small">Monthly Plan</Heading>
                      <p style={{ margin: '8px 0', fontSize: '24px', fontWeight: 'bold' }}>${monthlyPlan.price}/month</p>
                      <p style={{ color: '#637381', fontSize: '14px' }}>Billed monthly</p>
                      {/* {monthlyPlan.trialDays > 0 && (
                      <p style={{ color: '#008060', fontSize: '12px', marginTop: '4px' }}>
                        {monthlyPlan.trialDays} days free trial
                      </p>
                    )} */}
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTabChange(1)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTabChange(1);
                        }
                      }}
                      style={{
                        padding: '16px',
                        border: selectedTab === 1 || isCurrentActivePlan('yearly') ? '2px solid #008060' : '1px solid #e1e3e5',
                        borderRadius: '8px',
                        position: 'relative',
                        cursor: apiCalled ? 'not-allowed' : 'pointer',
                        opacity: apiCalled ? 0.6 : 1,
                        backgroundColor: selectedTab === 1 ? '#f1f8f5' : '#fff',
                      }}
                    >
                      <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', width: '100px' }}>
                        {isCurrentActivePlan('yearly') ? (
                          <Badge status="info">Current</Badge>
                        ) : (
                          <Badge status="success">Best Value</Badge>
                        )}
                      </div>
                      <Heading element="h4" size="small">Yearly Plan</Heading>
                      <p style={{ margin: '8px 0', fontSize: '24px', fontWeight: 'bold' }}>${yearlyPlan.price}/year</p>
                      {yearlyOriginalPrice && yearlyDiscount > 0 && (
                        <p style={{ color: '#008060', fontSize: '14px', fontWeight: '500' }}>
                          Save ${(yearlyOriginalPrice - yearlyPlan.price).toFixed(2)} annually
                        </p>
                      )}
                      {/* {yearlyPlan.trialDays > 0 && (
                      <p style={{ color: '#008060', fontSize: '12px', marginTop: '4px' }}>
                        {yearlyPlan.trialDays} days free trial
                      </p>
                    )} */}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

      </div>
      {toastMarkup}
    </Page >
  );
};

PricingPlanContainer.propTypes = {

};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    error: state.onboarding.error
  }
}

const mapDispatchToProps = {
  updateOnboarding
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PricingPlanContainer);